
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CouponCard } from '../components/CouponCard';
import { MAIN_COUPON_CODE, BATCHES, EXAM_CATEGORIES } from '../constants';
import { ArrowLeft, CheckCircle2, Info, ArrowRight, Star, Users, Calendar, Copy, Check, Loader2, GraduationCap, Box } from 'lucide-react';

interface PageData {
  title: string;
  keyword: string;
  description: string;
  longContent: string;
  color: 'primary' | 'secondary' | 'accent';
}

const DEFAULT_PAGE: PageData = {
  title: 'PW All Batches Coupon Code 2026',
  keyword: 'Physics Wallah General Offers',
  color: 'primary',
  description: 'The definitive list of coupon codes for every Physics Wallah batch including Foundation, JEE, NEET, and UPSC.',
  longContent: 'Physics Wallah offers education to millions across various domains. Using the universal code JITJAN0001 ensures you get the highest possible rebate on any batch you choose. From class 6-12 foundation batches to high-level competitive exam preparation, this code is your key to savings.'
};

const CouponPage: React.FC<{ pageId?: string }> = ({ pageId }) => {
  const { slug, categorySlug } = useParams<{ slug: string; categorySlug: string }>();
  const [applyingId, setApplyingId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [copiedCodeOnlyId, setCopiedCodeOnlyId] = useState<string | null>(null);

  const currentId = pageId || categorySlug || slug || 'pw-coupon-code';
  const isAllBatches = currentId === 'pw-coupon-code';
  const categoryInfo = EXAM_CATEGORIES.find(c => c.slug === currentId);

  // Set document title for SEO on category detail pages
  useEffect(() => {
    if (categoryInfo && categoryInfo.seoTitle) {
      document.title = categoryInfo.seoTitle;
    } else if (isAllBatches) {
      document.title = 'PW All Batches Coupon Code 2026 | Physics Wallah Discount';
    }
  }, [categoryInfo, isAllBatches]);

  const data: PageData = categoryInfo ? {
    title: categoryInfo.seoH1 || `PW ${categoryInfo.label} Coupon Code 2026`,
    keyword: `${categoryInfo.label} Batch Offers`,
    color: categoryInfo.slug.includes('jee') ? 'primary' : categoryInfo.slug.includes('neet') ? 'secondary' : 'accent',
    description: `Get exclusive discounts for ${categoryInfo.label} batches on Physics Wallah using verified code JITJAN0001.`,
    longContent: `Looking for ${categoryInfo.label} preparation? Physics Wallah provides the best-in-class coaching with top faculty. Use JITJAN0001 to get the maximum discount on your enrollment. Our trending ${categoryInfo.label} batches are updated for the 2026 session.`
  } : DEFAULT_PAGE;

  // Map old category identifiers to new slugs for batch filtering
  const getCategoryMatcher = (slug: string): string => {
    // Extract the exam name from SEO slug format (pw-xxx-coupon-code)
    const match = slug.match(/^pw-(.+)-coupon-code$/);
    if (match) {
      return match[1].replace(/-/g, '');
    }
    return slug.toLowerCase().replace(/[^a-z0-9]/g, '');
  };

  const filteredBatches = isAllBatches
    ? BATCHES
    : BATCHES.filter(b => {
        const batchCat = b.category.toLowerCase().replace(/[^a-z0-9]/g, '');
        const targetId = getCategoryMatcher(currentId);
        if (targetId === 'iitjee' && batchCat === 'iitjee') return true;
        if (targetId === 'neet' && batchCat === 'neet') return true;
        if (targetId === 'upsc' && batchCat === 'upsc') return true;
        return batchCat.includes(targetId) || targetId.includes(batchCat);
      });

  const handleApplyCoupon = (batchId: string) => {
    setApplyingId(batchId);
    navigator.clipboard.writeText(MAIN_COUPON_CODE);
    setCopiedId(batchId);
    setTimeout(() => {
      setApplyingId(null);
      setTimeout(() => setCopiedId(null), 2000);
    }, 600);
  };

  const handleCopyCodeOnly = (batchId: string) => {
    navigator.clipboard.writeText(MAIN_COUPON_CODE);
    setCopiedCodeOnlyId(batchId);
    setTimeout(() => setCopiedCodeOnlyId(null), 2000);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8 sm:py-24 animate-in fade-in zoom-in-95 duration-700 text-white relative z-10">
      {/* SEO Breadcrumbs */}
      <nav className="flex items-center gap-2 text-[9px] font-black uppercase tracking-widest text-gray-600 mb-8 sm:mb-12">
        <Link to="/" className="hover:text-white transition-colors">Home</Link>
        <span>/</span>
        <span className="text-blue-500 truncate">{data.title.slice(0, 20)}...</span>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-20 items-center mb-16 sm:mb-32">
        <div className="relative">
          <Box className="absolute -top-4 -left-4 sm:-top-12 sm:-left-12 text-blue-500/10 w-16 h-16 sm:w-40 sm:h-40 animate-float opacity-30 pointer-events-none" />
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-600/10 border border-blue-600/20 text-blue-400 text-[8px] sm:text-[10px] font-black mb-6 uppercase tracking-[0.2em] backdrop-blur-md">
            {data.keyword}
          </div>
          <h1 className="text-3xl sm:text-6xl md:text-8xl font-black mb-6 sm:mb-8 leading-[1.1] sm:leading-[0.9] tracking-tighter italic uppercase">{data.title}</h1>
          <p className="text-sm sm:text-xl text-gray-400 mb-8 sm:mb-10 leading-relaxed font-medium">
            {data.description} Our verified code <span className="text-blue-500 font-black italic underline decoration-blue-500/30">JITJAN0001</span> is now live for all 2026 enrollments.
          </p>
          <div className="flex flex-wrap gap-3 sm:gap-6">
            <div className="flex items-center gap-2 text-[8px] sm:text-[10px] font-black text-gray-400 uppercase tracking-widest bg-white/5 px-3 py-1.5 rounded-xl border border-white/10">
              <CheckCircle2 size={12} className="text-green-500" /> Verified Today
            </div>
            <div className="flex items-center gap-2 text-[8px] sm:text-[10px] font-black text-gray-400 uppercase tracking-widest bg-white/5 px-3 py-1.5 rounded-xl border border-white/10">
               <Users size={12} className="text-blue-500" /> 10k+ Uses
            </div>
          </div>
        </div>
        <div className="flex justify-center perspective-1000 scale-[0.85] sm:scale-100">
          <div className="transform-gpu hover:rotate-y-12 transition-transform duration-700">
            <CouponCard code={MAIN_COUPON_CODE} variant={data.color} title="Official Verified Code" />
          </div>
        </div>
      </div>

      <div className="mb-20 sm:mb-40">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 sm:gap-6 mb-10 sm:mb-16 relative">
          <div className="border-l-4 border-blue-600 pl-4 sm:pl-8">
            <h2 className="text-2xl sm:text-4xl font-black uppercase tracking-tighter text-white italic">
              {categoryInfo ? `Trending ${categoryInfo.label} Batches` : 'All Global Batches 2026'}
            </h2>
            <p className="text-gray-500 font-bold uppercase text-[7px] sm:text-[10px] tracking-widest mt-1">Maximum rebate applied via ambassador link</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-10">
          {filteredBatches.map((batch) => (
            <div key={batch.id} className="bg-[#111]/40 border border-white/5 rounded-[24px] sm:rounded-[48px] overflow-hidden group hover:border-blue-500/40 transition-all shadow-xl backdrop-blur-xl transform-gpu hover:-translate-y-2">
              <div className="aspect-[16/10] relative overflow-hidden">
                <img src={batch.image} alt={batch.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000 opacity-40 grayscale group-hover:grayscale-0" />
                <div className="absolute top-4 right-4 sm:top-6 sm:right-6">
                  {batch.trending && (
                    <span className="bg-yellow-400 text-black text-[7px] sm:text-[10px] font-black uppercase px-2 py-1 sm:px-4 sm:py-1.5 rounded-full flex items-center gap-1 shadow-2xl shadow-yellow-400/20 animate-pulse">
                      <Star size={10} className="fill-current" /> Hot
                    </span>
                  )}
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] to-transparent" />
                <div className="absolute bottom-4 left-4 sm:bottom-6 sm:left-8">
                  <span className="text-[7px] sm:text-[10px] font-black text-blue-400 bg-blue-400/10 px-2 py-1 rounded-full border border-blue-400/20 uppercase tracking-[0.1em] backdrop-blur-md">
                    {batch.category}
                  </span>
                </div>
              </div>
              <div className="p-5 sm:p-10">
                <h3 className="text-lg sm:text-2xl font-black mb-2 group-hover:text-blue-400 transition-colors text-white uppercase tracking-tight">{batch.name}</h3>
                <div className="flex flex-col gap-1 mb-6">
                  <span className="flex items-center gap-2 text-[9px] sm:text-xs text-gray-500 font-bold"><Calendar size={12} className="text-blue-500" /> {batch.target}</span>
                </div>
                <div className="flex flex-col gap-3">
                  <button 
                    onClick={() => handleApplyCoupon(batch.id)}
                    disabled={applyingId === batch.id}
                    className="w-full py-4 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600/50 text-white font-black uppercase text-[10px] sm:text-xs tracking-[0.1em] rounded-[14px] sm:rounded-[24px] transition-all active:scale-95 shadow-xl flex items-center justify-center gap-2"
                  >
                    {applyingId === batch.id ? (
                      <><Loader2 size={16} className="animate-spin" /> applying...</>
                    ) : (
                      <>
                        {copiedId === batch.id ? <Check size={16} /> : <Copy size={16} />}
                        {copiedId === batch.id ? 'Applied!' : 'Redeem Discount'}
                      </>
                    )}
                  </button>
                  <button 
                    onClick={() => handleCopyCodeOnly(batch.id)}
                    className="w-full py-3 bg-white/5 hover:bg-white/10 border border-white/10 text-gray-500 hover:text-white font-black uppercase text-[8px] sm:text-[10px] tracking-[0.1em] rounded-[14px] sm:rounded-[20px] transition-all active:scale-95 flex items-center justify-center gap-2"
                  >
                    {copiedCodeOnlyId === batch.id ? (
                      <><Check size={14} className="text-green-500" /> Copied</>
                    ) : (
                      <><Copy size={14} /> Copy Code Only</>
                    )}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* SEO Long-form content section */}
      <div className="bg-[#111]/40 border border-white/5 backdrop-blur-xl rounded-[30px] sm:rounded-[60px] p-6 sm:p-20 relative overflow-hidden mb-20 sm:mb-32 group">
        <div className="absolute -right-10 -bottom-10 w-[150px] h-[150px] sm:w-[400px] sm:h-[400px] bg-blue-600/5 blur-[50px] sm:blur-[100px] pointer-events-none group-hover:bg-blue-600/10 transition-colors" />
        <div className="flex items-center gap-3 sm:gap-5 mb-6 sm:mb-12 text-blue-400">
          <div className="bg-blue-600/10 p-3 sm:p-5 rounded-[18px] sm:rounded-[32px] border border-blue-600/20 shadow-2xl shadow-blue-600/5 animate-float"><Info size={20} className="sm:w-8 sm:h-8" /></div>
          <h2 className="text-xl sm:text-4xl font-black uppercase tracking-tighter italic">Enrollment Master Guide</h2>
        </div>
        <div className="prose prose-invert max-w-none text-gray-400 leading-relaxed">
          <p className="mb-6 sm:mb-12 text-sm sm:text-xl font-medium leading-relaxed italic">{data.longContent}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-10">
            <div className="p-6 sm:p-10 bg-black/40 border border-white/5 rounded-[20px] sm:rounded-[40px] transform-gpu hover:-translate-y-1 transition-transform">
              <span className="text-3xl sm:text-5xl font-black text-blue-600/10 mb-4 block font-mono italic">01</span>
              <h4 className="font-black text-white text-base sm:text-xl mb-2 sm:mb-4 uppercase tracking-tight">One-Tap Copy</h4>
              <p className="text-[10px] sm:text-sm font-medium">Click any redemption button on this page. Our system will instantly copy JITJAN0001 to your clipboard and verify its status for 2026.</p>
            </div>
            <div className="p-6 sm:p-10 bg-black/40 border border-white/5 rounded-[20px] sm:rounded-[40px] transform-gpu hover:-translate-y-1 transition-transform">
               <span className="text-3xl sm:text-5xl font-black text-blue-600/10 mb-4 block font-mono italic">02</span>
              <h4 className="font-black text-white text-base sm:text-xl mb-2 sm:mb-4 uppercase tracking-tight">Redeem on PW</h4>
              <p className="text-[10px] sm:text-sm font-medium">Head to the Physics Wallah app. Choose your batch (Arjuna, Lakshya, or Yakeen) and paste the code in the voucher field at checkout.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CouponPage;
